import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function VredehoekPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Vredehoek Painters Cape Town | Professional Painting Services | Urban Hillside Property Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Vredehoek properties. Expert hillside painting, urban property services, interior & exterior painting. Serving Vredehoek Cape Town with premium quality finishes and city view property expertise.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Vredehoek painters, hillside property painting, urban painting, Vredehoek property painting, professional painters Cape Town');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/vredehoek`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Vredehoek Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Vredehoek properties with hillside expertise and urban property specialization.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/vredehoek`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Vredehoek",
        "description": "Specialized painting services for Vredehoek's hillside properties and urban homes.",
        "areaServed": "Vredehoek, Cape Town",
        "serviceType": "Urban Hillside Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Vredehoek')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Hillside Property Painting',
      description: 'Specialized painting services for Vredehoek\'s hillside properties, addressing unique access challenges and slope considerations with expert solutions.',
      price: 'From R12,000',
      features: ['Slope access solutions', 'Safety equipment', 'Specialized techniques', 'Weather considerations']
    },
    {
      icon: 'ri-building-line',
      title: 'Urban Apartment Painting',
      description: 'Professional painting for Vredehoek apartments and flats with efficient service and minimal disruption to building residents.',
      price: 'From R8,000',
      features: ['Building coordination', 'Efficient scheduling', 'Minimal disruption', 'Quality finishes']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Painting',
      description: 'Expert interior painting services for Vredehoek properties including modern apartments and hillside homes with attention to detail.',
      price: 'From R7,000',
      features: ['Room preparation', 'Premium paints', 'Color consultation', 'Clean execution']
    },
    {
      icon: 'ri-palette-line',
      title: 'Exterior Painting',
      description: 'Professional exterior painting for Vredehoek properties, protecting against urban and hillside weather conditions while enhancing curb appeal.',
      price: 'From R10,000',
      features: ['Weather protection', 'Surface preparation', 'Durable coatings', 'City view enhancement']
    },
    {
      icon: 'ri-tools-line',
      title: 'Heritage Property Painting',
      description: 'Specialized painting for Vredehoek\'s older properties and heritage buildings, preserving character while providing modern protection.',
      price: 'From R11,000',
      features: ['Heritage preservation', 'Period techniques', 'Character retention', 'Modern protection']
    },
    {
      icon: 'ri-shield-line',
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting services to protect your Vredehoek property investment with scheduled inspections and preventive care.',
      price: 'From R5,000',
      features: ['Scheduled visits', 'Preventive maintenance', 'Priority service', 'Long-term protection']
    }
  ];

  const testimonials = [
    {
      name: 'Andrew Peterson',
      property: 'Hillside Home',
      rating: 5,
      text: 'Exceptional work on our Vredehoek hillside property. They handled the challenging access with professionalism and the results are outstanding. Highly recommend.',
      image: 'https://public.readdy.ai/ai/img_res/1b579e0e5a291afe236bedfbec10f68e.jpg'
    },
    {
      name: 'Rachel Cohen',
      property: 'City Apartment',
      rating: 5,
      text: 'Professional team with excellent attention to detail. They transformed our Vredehoek apartment beautifully and coordinated perfectly with building management.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20woman%20homeowner%20in%20Vredehoek%20apartment%20with%20city%20views%2C%20newly%20painted%20interior%2C%20satisfied%20client%20portrait&width=80&height=80&seq=vredehoek2&orientation=squarish'
    },
    {
      name: 'David Naidoo',
      property: 'Heritage Property',
      rating: 5,
      text: 'Outstanding restoration work on our older Vredehoek property. They preserved the character while providing modern protection. The quality is superb.',
      image: 'https://readdy.ai/api/search-image?query=Satisfied%20man%20homeowner%20at%20Vredehoek%20heritage%20property%20with%20restored%20painted%20exterior%2C%20happy%20customer%20portrait&width=80&height=80&seq=vredehoek5&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Hillside Home Renovation',
      description: 'Complete exterior and interior painting for Vredehoek hillside property with challenging access and spectacular city views',
      image: 'https://readdy.ai/api/search-image?query=Vredehoek%20hillside%20home%20with%20freshly%20painted%20exterior%2C%20city%20views%2C%20professional%20painting%20work%2C%20urban%20hillside%20property&width=400&height=300&seq=vredehoek3&orientation=landscape'
    },
    {
      title: 'Urban Apartment Complex',
      description: 'Multi-unit apartment painting project with coordinated service and efficient scheduling for minimal resident disruption',
      image: 'https://readdy.ai/api/search-image?query=Vredehoek%20apartment%20building%20with%20newly%20painted%20exterior%2C%20urban%20setting%2C%20professional%20maintenance%20work&width=400&height=300&seq=vredehoek4&orientation=landscape'
    },
    {
      title: 'Heritage Building Restoration',
      description: 'Careful restoration and painting of older Vredehoek building preserving architectural character with modern finishes',
      image: 'https://readdy.ai/api/search-image?query=Vredehoek%20heritage%20building%20with%20restored%20painted%20exterior%2C%20period%20architecture%2C%20professional%20restoration%20work&width=400&height=300&seq=vredehoek6&orientation=landscape'
    },
    {
      title: 'Modern City Home',
      description: 'Contemporary interior and exterior painting for Vredehoek urban home with sleek finishes and city view enhancement',
      image: 'https://readdy.ai/api/search-image?query=Modern%20Vredehoek%20home%20with%20contemporary%20painted%20exterior%2C%20city%20views%2C%20professional%20painting%20finish&width=400&height=300&seq=vredehoek7&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Site Assessment',
      description: 'Comprehensive evaluation of your Vredehoek property including hillside access, urban considerations, and specific requirements.'
    },
    {
      step: '2',
      title: 'Access Planning',
      description: 'Development of specialized access solutions for hillside properties and coordination with building management for apartments.'
    },
    {
      step: '3',
      title: 'Safety Preparation',
      description: 'Implementation of appropriate safety measures for hillside work and urban environments ensuring team and property protection.'
    },
    {
      step: '4',
      title: 'Professional Application',
      description: 'Expert painting using specialized techniques for hillside and urban properties with quality materials and proven methods.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Thorough inspection ensuring every aspect meets our high standards despite challenging access or urban constraints.'
    },
    {
      step: '6',
      title: 'Maintenance Guidance',
      description: 'Comprehensive care instructions and maintenance recommendations specific to your Vredehoek property\'s unique location.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20hillside%20property%20in%20Vredehoek%20with%20Table%20Mountain%20and%20city%20views%2C%20painting%20equipment%2C%20urban%20setting%2C%20beautiful%20Cape%20Town%20scenery&width=1920&height=1080&seq=vredehoekhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Vredehoek
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Vredehoek hillside properties and urban homes. Specialized access solutions and city view property painting for your home with professional results and spectacular finishes that enhance your property's unique location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Vredehoek Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Hillside Painting Excellence in Vredehoek
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Vredehoek is one of Cape Town's most distinctive urban neighborhoods, perched on the slopes of Table Mountain with spectacular views over the city bowl and harbor. This vibrant area is known for its unique hillside character, diverse architectural styles, and proximity to the city center. The suburb attracts young professionals, families, and urban dwellers who appreciate the combination of mountain living, city convenience, and stunning vistas. Vredehoek features a mix of property types from modern apartments to older homes and heritage buildings, creating a dynamic and cosmopolitan community.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The hillside location of Vredehoek presents unique challenges and opportunities for painting services. Properties built on slopes require specialized access solutions, safety equipment, and techniques that differ from standard painting projects. The urban environment combined with mountain exposure means properties face diverse weather conditions including wind, rain, and intense sun, requiring durable protective coatings. Many of Vredehoek's older properties feature interesting architectural details and heritage elements that demand careful attention and appropriate restoration techniques.
              </p>
              <p className="text-lg text-gray-600">
                Our team has extensive experience working with Vredehoek's challenging hillside properties and understands the specific requirements of this unique location. We provide specialized access solutions for steep slopes, coordinate effectively with apartment building management, and deliver quality results that enhance your property's value while respecting its character. Whether your property is a modern apartment with city views or a heritage home on the mountainside, we provide professional service that addresses the unique challenges of Vredehoek's distinctive urban hillside environment.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Vredehoek%20Cape%20Town%20hillside%20neighborhood%20with%20Table%20Mountain%20backdrop%2C%20city%20views%2C%20urban%20hillside%20properties%2C%20diverse%20architecture&width=600&height=400&seq=vredehoek8&orientation=landscape"
                alt="Vredehoek Hillside Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Vredehoek Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Vredehoek's unique hillside properties and urban environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Vredehoek Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A specialized approach ensuring exceptional results for your Vredehoek hillside or urban property.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Vredehoek */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Vredehoek?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in hillside and urban property painting with proven results and satisfied Vredehoek residents.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-mountain-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Hillside Specialists</h3>
              <p className="text-gray-600">Extensive experience with Vredehoek\'s challenging hillside properties, providing specialized access solutions and safety measures for slope work.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-building-4-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Urban Expertise</h3>
              <p className="text-gray-600">Skilled in urban property painting with experience coordinating with building management and working in dense residential areas.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Safety Priority</h3>
              <p className="text-gray-600">Comprehensive safety protocols for hillside work including specialized equipment and trained personnel ensuring secure project execution.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-eye-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">View Enhancement</h3>
              <p className="text-gray-600">Expert color consultation considering Vredehoek\'s spectacular views, ensuring your property\'s finishes complement its unique location.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Vredehoek Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Vredehoek hillside and urban property painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Vredehoek Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Vredehoek property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hillside Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding Vredehoek's Unique Challenges
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Specialized Solutions We Provide</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-road-map-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Difficult Access Solutions</h4>
                    <p className="text-gray-600">Specialized equipment and techniques for reaching hillside properties with steep slopes, narrow access, and challenging terrain unique to Vredehoek\'s mountainside location.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-star-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Enhanced Safety Protocols</h4>
                    <p className="text-gray-600">Comprehensive safety measures for hillside work including harnesses, scaffolding systems, and trained personnel experienced in slope painting projects.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-windy-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Weather Adaptation</h4>
                    <p className="text-gray-600">Flexible scheduling and specialized techniques to work around Vredehoek\'s variable mountain weather including wind, rain, and rapid temperature changes.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-building-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Urban Coordination</h4>
                    <p className="text-gray-600">Effective coordination with apartment building management, body corporates, and neighbors ensuring smooth project execution in dense urban environment.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Vredehoek%20hillside%20property%20with%20challenging%20access%2C%20steep%20slopes%2C%20mountain%20setting%2C%20professional%20painting%20equipment%20and%20safety%20measures&width=600&height=500&seq=vredehoek9&orientation=portrait"
                alt="Vredehoek Hillside Challenges"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Vredehoek Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's hillside and urban property painting specialists for Vredehoek. Our expert team understands the unique challenges of mountainside properties and is ready to provide you with a comprehensive consultation and detailed quote. Experience the difference that specialized hillside painting expertise makes for your Vredehoek property with professional access solutions and quality results that enhance your spectacular views.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
